import { transition } from '@helpers/animation';
import brand, { colourWithTransparency } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { between, Device, from, until } from '@helpers/media';
import TagStyles from '@stories/Components/Tag/Tag.styles';
import styled, { css } from 'styled-components';
import CarouselButtonsStyles from '../CarouselButtons/CarouselButtons.styles';
const ImageContainer = styled.div `
  position: relative;
  border-radius: 5px 5px 0 0;
  height: 0;
  width: 100%;

  [data-image-ratio='4:3'] & {
    padding-bottom: calc(100% * (3 / 4));
  }

  [data-image-ratio='16:9'] & {
    padding-bottom: calc(100% * (9 / 16));
  }

  ${TagStyles.Container} {
    ${fonts.inter.extraBold};

    position: absolute;
    top: 0;
    bottom: auto;
    left: 0;
    border-width: 0 ${fluid(1, 2)} ${fluid(1, 2)} 0;
    border-radius: 4px 0 20px 0;
    z-index: 2;
  }

  ${CarouselButtonsStyles.Container} {
    ${transition('opacity', 0.5)};

    opacity: 0;

    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  &:focus-within,
  &:hover {
    ${CarouselButtonsStyles.Container} {
      opacity: 1;
    }
  }

  ${CarouselButtonsStyles.CarouselButtonPrev},
  ${CarouselButtonsStyles.CarouselButtonNext} {
    position: absolute;
    top: 50%;
    width: 40px;
    height: 40px;

    svg {
      width: 10px;
      height: 16px;
    }
  }

  ${CarouselButtonsStyles.CarouselButtonPrev} {
    left: 8px;
    transform: translateY(-50%) scale(-1);
  }

  ${CarouselButtonsStyles.CarouselButtonNext} {
    right: 8px;
    transform: translateY(-50%);
  }
`;
const CarouselDotsContainer = styled.div `
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
`;
const CarouselDot = styled.div `
  width: 15px;
  height: 5px;
  border-radius: 5px;
  background-color: ${brand.primary.grey.base};
  margin-right: 10px;
  cursor: pointer;

  ${({ active }) => active &&
    css `
      background-color: ${brand.primary.blue.base};
    `}
`;
const Image = styled.img `
  ${transition('opacity', 0.5)};

  position: absolute;
  top: 0;
  left: 0;
  display: block;
  border-radius: 5px 5px 0 0;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
  opacity: 0;

  &[data-active='true'] {
    opacity: 1;
  }
`;
const ContentContainer = styled.div `
  padding: 20px 20px 0;
  background-color: ${brand.white};
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const Title = styled.h3 `
  ${fonts.inter.extraBold};

  display: block;
  color: ${brand.black};
  font-size: 16px;
  line-height: 26px;
  margin-bottom: 8px;
`;
const Subtitle = styled.span `
  display: block;
  color: ${brand.black};
  ${fonts.sofiaPro.regular};
  font-size: 18px;
  line-height: 26px;
  margin-bottom: 16px;
`;
const Strapline = styled.span `
  display: block;
  color: ${brand.black};
  ${fonts.sofiaPro.regular};
  font-size: 18px;
  line-height: 28px;
  margin-bottom: 16px;
`;
const PriceContainer = styled.div `
  display: flex;
  margin-bottom: 16px;
  margin-top: auto;
`;
const PriceItem = styled.div `
  margin-right: 48px;

  &:last-child {
    margin-right: 0;
  }
`;
const PriceLabel = styled.div `
  ${fonts.sofiaPro.medium};
  font-size: 16px;
  color: ${brand.primary.grey.base};
  margin-bottom: 5px;
`;
const Price = styled.div `
  ${fonts.sofiaPro.semiBold};
  font-size: 16px;
  color: ${brand.black};
`;
const FeaturesContainer = styled.div `
  padding: 10px 0 20px;
  background-color: ${brand.white};
  border-radius: 0 0 5px 5px;
  margin-top: auto;
  margin-left: 20px;
  margin-right: 20px;
  border-top: 1px solid ${brand.primary.grey.light};
`;
const Features = styled.div `
  display: grid;
  flex-wrap: wrap;
  border-bottom: 1px solid ${brand.primary.grey.light};
  grid-template-columns: repeat(2, 1fr);

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const Feature = styled.div `
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-right: 8px;
`;
const FeatureIconImage = styled.img `
  margin-right: 8px;
  width: 30px;
  height: 30px;

  @media ${from(Device.DesktopSmall)} {
    width: 25px;
    height: 25px;
  }

  @media ${from(Device.DesktopLarge)} {
    width: 35px;
    height: 35px;
  }
`;
const FeatureText = styled.div `
  ${fonts.sofiaPro.medium};
  font-size: 14px;
  color: ${brand.black};

  @media ${between(Device.Desktop, Device.DesktopLarge)} {
    font-size: 12px;
  }
`;
const LocationContainer = styled.div `
  flex: 1 0 30%;
  display: flex;
  align-items: center;
`;
const LocationTooltip = styled.div `
  ${fonts.sofiaPro.regular};
  font-size: 14px;
  line-height: 18px;
  display: none;
  position: absolute;
  background-color: ${brand.black};
  color: ${brand.white};
  padding: 10px;
  text-align: center;
  top: 40px;
  min-width: 170px;
  z-index: 10;
  border-radius: 5px;
  left: 50%;
  transform: translateX(-50%);
`;
const LocationIcon = styled.div `
  margin-left: 8px;
  margin-bottom: 8px;
  color: ${brand.black};
  position: relative;
  cursor: pointer;

  svg {
    width: 15px;
    height: 22px;
  }

  &:hover {
    svg {
      color: ${brand.primary.blue.base};
    }

    ${LocationTooltip} {
      display: block;
    }
  }
`;
const LinkContainer = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;
const ShortlistContainer = styled.div `
  color: ${brand.white};
  cursor: pointer;

  svg {
    width: 35px;
    height: 35px;
    --background-color: ${brand.black};
    --heart-color: ${brand.white};

    ${({ added }) => added &&
    css `
        --heart-color: ${brand.primary.blue.light};
      `}
  }

  &:hover {
    svg {
      --heart-color: ${brand.primary.blue.light};

      ${({ added }) => added &&
    css `
          --heart-color: ${brand.white};
        `}
    }
  }
`;
const Link = styled.a `
  ${fonts.inter.extraBold};

  font-size: 14px;
  color: ${brand.primary.blue.base};
  text-decoration: none;
  display: flex;
  gap: 8px;
  align-items: baseline;

  span& {
    opacity: 0.5;
    pointer-events: none;
  }

  svg {
    width: 7px;
  }

  @media ${from(Device.TabletLarge)} {
    font-size: 20px;
    align-items: flex-end;

    svg {
      width: 9px;
    }
  }
`;
const UnstyledLink = styled.a `
  text-decoration: none;
  height: 100%;
`;
const Container = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 4px ${colourWithTransparency(brand.black, 0.25)};
  border-radius: 4px;
  text-decoration: none;
  height: 100%;

  ${({ listView }) => listView &&
    css `
      @media ${from(Device.TabletLarge)} {
        display: flex;
        flex-direction: row;
        box-shadow: 0 0 4px ${colourWithTransparency(brand.black, 0.25)};

        ${ImageContainer} {
          max-width: 410px;
          height: 100%;
          max-height: 231px;
          padding-bottom: 0;
          border-radius: 5px 0 0 5px;
        }

        ${ContentContainer},
        ${FeaturesContainer} {
          padding: 20px;
        }

        ${FeaturesContainer} {
          display: flex;
          flex-direction: column;
          border-radius: 0 5px 5px 0;
          padding: 0;
          margin-top: unset;
          margin-right: 0;
          margin-left: auto;
          border-left: 1px solid ${brand.primary.grey.light};
          border-top: 0;
          width: 100%;
          max-width: 260px;
        }

        ${Subtitle} {
          margin-bottom: 32px;
        }

        ${PriceContainer} {
          border-bottom: none;
          margin-bottom: 0;
        }

        ${PriceLabel}, ${Price} {
          font-size: 18px;
        }

        ${Price} {
          ${fonts.inter.extraBold};

          font-size: 14px;
        }

        ${Features} {
          grid-template-columns: repeat(2, 1fr);
          padding: 20px 20px 16px;
        }

        ${Feature} {
          flex: 1 1 50%;
        }

        ${LocationContainer} {
          flex: 1 1 50%;
        }

        ${Image} {
          height: 100%;
          max-height: 231px;
          border-radius: 5px 0 0 5px;

          &[data-active='true'] {
            position: relative;
          }
        }

        ${LinkContainer} {
          height: 100%;
          align-items: center;
          padding: 20px;
          margin-top: 0;
        }
      }

      @media ${from(Device.Desktop)} and ${until(Device.DesktopLarge)} {
        ${ContentContainer},
        ${FeaturesContainer} {
          padding: 15px;
        }
      }
    `}
`;
const ImageCount = styled.p `
  ${fonts.sofiaPro.medium};

  display: flex;
  align-items: center;
  gap: ${fluid(6, 8)};
  position: absolute;
  top: ${fluid(4, 8)};
  right: ${fluid(8, 10)};
  padding: 6px 8px;
  border-radius: 8px;
  background-color: ${brand.primary.blue.base};
  color: ${brand.white};
  font-size: ${fluid(14, 16)};
  margin: 0;
  z-index: 1;

  svg {
    color: currentColor;
    display: block;
    height: ${fluid(12, 14)};
  }

  span:nth-child(2) {
    transform: translateY(-10%);
  }
`;
export const UsedCarStyles = {
    CarouselDot,
    CarouselDotsContainer,
    Container,
    ContentContainer,
    Feature,
    FeatureIconImage,
    Features,
    FeaturesContainer,
    FeatureText,
    Image,
    ImageContainer,
    ImageCount,
    Link,
    LinkContainer,
    LocationContainer,
    LocationIcon,
    LocationTooltip,
    Price,
    PriceContainer,
    PriceItem,
    PriceLabel,
    ShortlistContainer,
    Strapline,
    Subtitle,
    Title,
    UnstyledLink,
};
